// client/src/components/auth/SessionTimeout.js - TRADOTTO
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import tokenService, {
  IDLE_TIMEOUT,
  IDLE_WARNING,
  SESSION_MAX,
  SESSION_END_WARNING
} from '../../services/tokenService';

// Avvisi e logout automatici della sessione:
// - inattività: avviso a IDLE_TIMEOUT - IDLE_WARNING, logout a IDLE_TIMEOUT
// - durata massima: avviso SESSION_END_WARNING prima, logout alla scadenza del token
const SessionTimeout = () => {
  const navigate = useNavigate();
  const { logout, isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const [warning, setWarning] = useState(null); // null | 'idle' | 'sessionEnd'
  const [timeLeft, setTimeLeft] = useState(0);  // secondi

  // FLAG per prevenire logout multipli
  const isLoggingOut = useRef(false);
  // sessionStart della sessione per cui l'avviso di fine sessione è già stato chiuso
  const sessionEndDismissedFor = useRef(null);

  // Format remaining time as mm:ss
  const formatTimeLeft = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
  };

  const handleLogout = useCallback((reason) => {
    if (isLoggingOut.current) {
      return;
    }
    isLoggingOut.current = true;
    setWarning(null);

    try {
      logout(reason);
      navigate('/login');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('SessionTimeout logout error:', error);
      }
    }
  }, [logout, navigate]);

  // Check session state every second
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setWarning(null);
      return;
    }

    isLoggingOut.current = false;

    const checkSession = () => {
      if (isLoggingOut.current) {
        return;
      }

      const now = Date.now();
      const idle = tokenService.getIdleTime();
      const info = tokenService.getTokenInfo();

      if (idle >= IDLE_TIMEOUT) {
        handleLogout('idle');
        return;
      }

      // Token scaduto: durata massima raggiunta o rinnovo non riuscito
      if (info && now >= info.expiresAt) {
        handleLogout('expired');
        return;
      }

      if (idle >= IDLE_TIMEOUT - IDLE_WARNING) {
        // Durante l'avviso conta solo il click su "Continua sessione"
        tokenService.setActivityLocked(true);
        setWarning('idle');
        setTimeLeft(Math.ceil((IDLE_TIMEOUT - idle) / 1000));
        return;
      }
      tokenService.setActivityLocked(false);

      if (info) {
        const sessionLeft = info.sessionStart + SESSION_MAX - now;
        if (sessionLeft <= SESSION_END_WARNING && sessionEndDismissedFor.current !== info.sessionStart) {
          setWarning('sessionEnd');
          setTimeLeft(Math.max(0, Math.ceil(sessionLeft / 1000)));
          return;
        }
      }

      setWarning(null);
    };

    checkSession();
    const checkIntervalId = setInterval(checkSession, 1000);

    return () => {
      clearInterval(checkIntervalId);
      tokenService.setActivityLocked(false);
    };
  }, [isAuthenticated, user, handleLogout]);

  // Continue session: counts as activity and renews the token immediately
  const handleContinueSession = () => {
    setWarning(null);
    tokenService.manualRefresh().catch(() => {});
  };

  // Session end warning can only be acknowledged, the session cannot be extended
  const handleDismissSessionEnd = () => {
    const info = tokenService.getTokenInfo();
    sessionEndDismissedFor.current = info ? info.sessionStart : null;
    setWarning(null);
  };

  // Don't render anything if no warning is shown or if logging out
  if (!warning || isLoggingOut.current) return null;

  const isIdle = warning === 'idle';

  // Render warning dialog
  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div className="modal" style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        maxWidth: '400px',
        width: '90%'
      }}>
        <div className="modal-header" style={{ marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, color: '#d32f2f' }}>
            {isIdle ? t('auth.sessionTimeout.title') : t('auth.sessionTimeout.sessionEndTitle')}
          </h3>
        </div>
        <div className="modal-body" style={{ marginBottom: '1.5rem' }}>
          <p style={{ margin: '0 0 1rem 0' }}>
            {isIdle ? t('auth.sessionTimeout.message') : t('auth.sessionTimeout.sessionEndMessage')}
          </p>
          <p style={{ margin: 0, fontSize: '1.1rem' }}>
            {isIdle ? t('auth.sessionTimeout.timeLeft') : t('auth.sessionTimeout.sessionEndTimeLeft')}{' '}
            <strong style={{ color: '#d32f2f' }}>{formatTimeLeft()}</strong>
          </p>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-secondary"
            onClick={() => handleLogout()}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ccc',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {t('auth.sessionTimeout.logoutNow')}
          </button>
          <button
            className="btn btn-primary"
            onClick={isIdle ? handleContinueSession : handleDismissSessionEnd}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              backgroundColor: '#1976d2',
              color: 'white',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {isIdle ? t('auth.sessionTimeout.continueSession') : t('auth.sessionTimeout.understood')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeout;
